import '../../components/overlays/overlays.scss';

import React, { useState, PropsWithChildren, ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { usePopper } from 'react-popper';
import { useOutsideClick, useOutsideScroll, useDelayedUnmount } from '../../components/hooks';

import { Maybe } from '../../core';
import classNames from 'classnames';
import { Placement } from '@popperjs/core/lib';
import { Options as OffsetOptions } from '@popperjs/core/lib/modifiers/offset';

type TooltipProps = PropsWithChildren<{
  id?: string;
  className?: Maybe<string>,
  arrowClassName?: Maybe<string>,
  referenceClassName?: Maybe<string>,
  content?: string | ReactNode,
  placement?: Placement,
  offset?: OffsetOptions["offset"],
  allowedAutoPlacements?: Placement[],
  fallbackPlacements?: Placement[],
  trigger?: 'click' | 'hover'
}>;

export const Tooltip = observer(({
  placement = 'top',
  offset = [0, 8],
  id,
  allowedAutoPlacements,
  fallbackPlacements,
  trigger = 'hover',
  ...props
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);
  const { shouldRender, isAnimating } = useDelayedUnmount(isVisible, 400);

  const className = classNames('tooltip menu-animate', props.className, isAnimating);
  const referenceClassName = classNames('ref-element', props.referenceClassName, {
    'active': isVisible
  });
  const arrowClassName = classNames('tooltip-arrow', props.arrowClassName);

  const isTriggerHover = trigger === 'hover';
  const isTriggerClick = trigger === 'click';

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: placement,
    strategy: 'fixed',
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement
        }
      },
      {
        name: 'offset',
        options: {
          offset: offset,
        }
      },
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: allowedAutoPlacements || ['top', 'bottom'],
          fallbackPlacements: fallbackPlacements || ['top', 'left', 'right', 'bottom'],
        },
      }
    ],
  });

  useOutsideClick(popperElement, () => setIsVisible(false));
  useOutsideScroll(popperElement, () => setIsVisible(false));

  return (
    <>
      <span className={referenceClassName}
        ref={setReferenceElement}
        onMouseEnter={() => isTriggerHover && setIsVisible(true)}
        onMouseLeave={() => isTriggerHover && setIsVisible(false)}
        onClick={() => isTriggerClick && setIsVisible(true)}>
        {props.children}
      </span>

      {shouldRender && (
        <div
          id={id}
          className={className}
          ref={setPopperElement}
          style={styles.popper} {...attributes.popper}>
          <div className={arrowClassName} ref={setArrowElement} style={styles.arrow} />
          <div className="tooltip-content">{props.content}</div>
        </div>
      )}
    </>
  );
});
